/**
 * 客户端资源隔离
 * 每个 handler 独立内存/CPU 配额
 */
export interface ResourceQuota {
  maxMemoryMB?: number
  maxCpuMs?: number
  maxConcurrency?: number
}

export interface HandlerExecution {
  handler: string
  startedAt: number
  memoryUsed: number
  cpuUsed: number
}

export class ResourceIsolation {
  private quotas = new Map<string, ResourceQuota>()
  private active = new Map<string, HandlerExecution[]>()
  private violations: Array<{ handler: string; type: string; timestamp: number }> = []

  /** 设置 handler 配额 */
  setQuota(handler: string, quota: ResourceQuota): void {
    this.quotas.set(handler, quota)
  }

  /** 获取 handler 配额 */
  getQuota(handler: string): ResourceQuota | null {
    return this.quotas.get(handler) ?? null
  }

  /** 检查是否允许执行 */
  canExecute(handler: string): boolean {
    const quota = this.quotas.get(handler)
    if (!quota) return true
    const active = this.active.get(handler) ?? []
    if (quota.maxConcurrency && active.length >= quota.maxConcurrency) {
      this.recordViolation(handler, 'concurrency')
      return false
    }
    return true
  }

  /** 记录执行开始 */
  startExecution(handler: string): string {
    const exec: HandlerExecution = {
      handler,
      startedAt: Date.now(),
      memoryUsed: 0,
      cpuUsed: 0,
    }
    const list = this.active.get(handler) ?? []
    list.push(exec)
    this.active.set(handler, list)
    return `${handler}_${exec.startedAt}`
  }

  /** 记录执行完成 */
  endExecution(handler: string, cpuMs: number = 0, memoryMB: number = 0): void {
    const list = this.active.get(handler) ?? []
    list.shift()
    if (list.length === 0) this.active.delete(handler)
    else this.active.set(handler, list)

    const quota = this.quotas.get(handler)
    if (quota) {
      if (quota.maxCpuMs && cpuMs > quota.maxCpuMs) this.recordViolation(handler, 'cpu')
      if (quota.maxMemoryMB && memoryMB > quota.maxMemoryMB) this.recordViolation(handler, 'memory')
    }
  }

  /** 获取活跃执行数 */
  getActiveCount(handler: string): number {
    return this.active.get(handler)?.length ?? 0
  }

  /** 获取违规记录 */
  getViolations(handler?: string): typeof this.violations {
    if (handler) return this.violations.filter((v) => v.handler === handler)
    return [...this.violations]
  }

  /** 清除违规记录 */
  clearViolations(): void {
    this.violations = []
  }

  private recordViolation(handler: string, type: string): void {
    this.violations.push({ handler, type, timestamp: Date.now() })
  }
}
